import React from "react";
import classnames from "classnames";
import { Button, Col, Input, InputGroup, Label, Row } from "reactstrap";

/**
 * faq building block (SCRUM-642, VariableField registry) — collapsible
 * `<details>` list of question/answer pairs. Byte-identical port of the three
 * legacy faq branches pinned by SCRUM-630/640:
 *
 * - Wizard (create): IndexTemplateSelector.jsx lines 1687–1773
 *   (contentVariables.types.test.jsx "faq: question input + answer textarea…")
 * - Update required: the former Required host (.jsx) lines 1253–1317
 *   (handleContentRequired.composites.test.jsx "faq — …")
 * - Update optional: the former Optional host (.jsx) lines 1050–1114
 *   (handleContentOptional.types.test.jsx "faq — …"; character-identical to
 *   the required branch, so `variant="update"` covers both)
 * - Stored-at-publish: e2e variableBaselineComposites.spec.ts — the plain
 *   `[{question, answer}]` array round-trips unnormalized; the per-item
 *   `name` attributes (`${variable}[${i}].question` / `.answer`) are
 *   load-bearing e2e selectors.
 *
 * Emits `onChange(settingVar.variable, itemsArray)` with the canonical plain
 * array. Host reporting envelopes are step-4 concerns: the wizard adapts to
 * `onChangeVariableValue({target: {name, value}})` (preserving its in-place
 * Content mutation), the update surfaces to `setVariables` +
 * `props.onVariableChange(updatedVariable)`.
 *
 * Variant differences (per the spec's per-surface table):
 * - create: `<details className="faq-container" data-collapsible>` CLOSED by
 *   default; answer placeholder `insert value for ${variable}` (dynamic);
 *   BARE controlled values (`item.question` / `item.answer`, no `|| ''`).
 * - update: `<details className="faq-container mb-3" open>` (key left to the
 *   parent); answer placeholder "Type your answer here…"; `|| ''` fallbacks.
 * - `summaryProps` is spread on the `<summary>` so the wizard host can pass
 *   dragHandleProps ('faq' ∈ NEEDS_HANDLE_BY_TYPE — the header is the drag
 *   handle, keeping the inputs clickable); replaces the legacy `handleProps`
 *   spread one-for-one.
 *
 * Preserved quirks (do NOT clean up):
 * - `Array.isArray` fallback WITHOUT a length check: `value: []` renders ZERO
 *   items (summary + "+ Add section" only); `""`/null/object renders one
 *   blank pair. The fallback pair is render-only — nothing is emitted until
 *   the first edit, which materializes the full array.
 * - removeItem has NO minimum guard: removing the last item emits `[]`.
 * - Index-based React keys on items.
 * - Remove/add Buttons pass NO explicit `type` prop (unlike infotext's
 *   `type="button"`); reactstrap 9 injects `type="button"` for onClick
 *   buttons either way, so the rendered DOM matches the legacy branches.
 * - Placeholder ellipses are U+2026 ("Type your question here…").
 * - Question is a reactstrap `Input`; answer is a RAW `<textarea rows=5
 *   cols=70>` with no className inside `InputGroup.input-group-merge`.
 * - The `varible_container` typo class on summary and rows.
 */
export type FaqItem = { question?: string; answer?: string };

export type FaqFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onChange: (name: string | undefined, value: FaqItem[]) => void;
  variant?: "create" | "update";
  summaryProps?: Record<string, unknown>;
};

export default function FaqField({
  settingVar,
  onChange,
  variant = "update",
  summaryProps = {},
}: FaqFieldProps) {
  const fieldName = settingVar.variable;
  const isCreate = variant === "create";
  const items: FaqItem[] = Array.isArray(settingVar.value)
    ? settingVar.value
    : [{ question: "", answer: "" }];

  const setItems = (next: FaqItem[]) => onChange(fieldName, next);

  const updateItem =
    (idx: number, field: keyof FaqItem) =>
    (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newVal = evt.target.value;
    const newItems = items.map((item, i) =>
      i === idx ? { ...item, [field]: newVal } : item
    );
    setItems(newItems);
  };

  const addItem = () => setItems([...items, { question: "", answer: "" }]);
  const removeItem = (idx: number) => setItems(items.filter((_, i) => i !== idx));

  const detailsProps = isCreate
    ? { className: "faq-container", "data-collapsible": true }
    : { className: "faq-container mb-3", open: true };

  return (
    <details {...detailsProps}>
      <summary
        className="varible_container form-control-label d-flex align-items-center"
        {...summaryProps}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        {fieldName}
      </summary>

      <div className="pt-3">
        {items.map((item, i) => (
          // index key: controlled row, no row-local state (SCRUM-2183)
          <div key={i} className="faq-item">
            <Row className="mb-3 varible_container">
              <Label md="2" className="form-control-label">
                Question #{i + 1}
              </Label>
              <Col md="9">
                <Input
                  name={`${fieldName}[${i}].question`}
                  type="text"
                  value={isCreate ? item.question : item.question || ""}
                  placeholder="Type your question here…"
                  onChange={updateItem(i, "question")}
                />
              </Col>
              <Col md="1">
                <Button color="danger" size="sm" onClick={() => removeItem(i)}>
                  &times;
                </Button>
              </Col>
            </Row>

            <Row className="varible_container">
              <Label md="2" className="form-control-label">
                Answer #{i + 1}
              </Label>
              <Col md="10">
                <InputGroup className={classnames("input-group-merge")}>
                  <textarea
                    rows={5}
                    cols={70}
                    placeholder={
                      isCreate
                        ? `insert value for ${fieldName}`
                        : "Type your answer here…"
                    }
                    value={isCreate ? item.answer : item.answer || ""}
                    onChange={updateItem(i, "answer")}
                    name={`${fieldName}[${i}].answer`}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>
        ))}

        <Row className="mt-4">
          <Col md={{ offset: 2, size: 10 }}>
            <Button color="primary" onClick={addItem}>
              + Add section
            </Button>
          </Col>
        </Row>
      </div>
    </details>
  );
}

// renders its own <summary>/header chrome - the dispatcher skips its label row (SCRUM-643)
FaqField.ownsChrome = true;
